import React, { useEffect, useState } from 'react';
import { ReactComponent as UploadExcelIcon } from '../../../../Assets/Icons/csvupload.svg';
import { ReactComponent as SampleUploadIcon } from '../../../../Assets/Icons/SampleUploadIcon.svg';
import { ReactComponent as DownloadIcon } from '../../../../Assets/Icons/downloadIcon.svg';
import { ReactComponent as TickIcon } from '../../../../Assets/Icons/whiteplaintick.svg';
import { ReactComponent as InfoIcon } from '../../../../Assets/Icons/info.svg';
import { ReactComponent as BlueCrossIcon } from '../../../../Assets/Icons/blueCross.svg';
import Checkbox from '../../Checkbox';

const UploadCSVDrawer = ({
    onClose,
    onUploadSuccess,
    isSubmitting,
    sampleFileUrl = "/sample-locations.csv",
    locations = [], // All locations
    selectedLocationIds = [],
    onSelectionChange, // Function to handle selection updates (single)
    onBatchSelectionChange // Function to handle batch selection
}) => {
    const fileInputRef = React.useRef(null);
    const [uploadedFile, setUploadedFile] = React.useState(null);

    // Filter for locations that appear to be from CSV (temp_)
    const csvLocations = locations.filter(loc => loc._id && loc._id.toString().startsWith('temp_'));
    const hasCsvLocations = csvLocations.length > 0;

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploadedFile(file);
            if (onUploadSuccess) {
                onUploadSuccess(event);
            }
        }
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setUploadedFile(null);
    };

    const handleSampleDownload = async () => {
        try {
            const publicBase = process.env.PUBLIC_URL || '';
            const url = sampleFileUrl.startsWith('http') ? sampleFileUrl : `${publicBase}${sampleFileUrl}`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch sample file');
            const blob = await response.blob();

            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = 'sample_locations.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error('Error downloading sample file:', error);
        }
    };

    const handleToggleLocation = (loc) => {
        if (onSelectionChange) {
            onSelectionChange(loc._id, loc.name, loc);
        }
    };

    const handleSelectAll = (e) => {
        const nextState = !allSelected;

        if (onBatchSelectionChange) {
            onBatchSelectionChange(csvLocations, nextState);
        } else if (onSelectionChange) {
            csvLocations.forEach(loc => {
                const isSelected = selectedLocationIds.includes(loc._id);
                if (nextState && !isSelected) {
                    onSelectionChange(loc._id, loc.name, loc); // Select
                } else if (!nextState && isSelected) {
                    onSelectionChange(loc._id, loc.name, loc); // Deselect
                }
            });
        }
    };

    // Derived state for "Select All" checkbox
    const allSelected = csvLocations.length > 0 && csvLocations.every(loc => selectedLocationIds.includes(loc._id));
    const selectedCount = csvLocations.filter(loc => selectedLocationIds.includes(loc._id)).length;

    return (
        <div className="drawer-content-wrapper flex flex-col h-full">
            <input
                type="file"
                ref={fileInputRef}
                hidden
                accept=".csv, .xls, .xlsx"
                onChange={handleFileChange}
                onClick={(e) => { e.target.value = null; }}
            />

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-1 mt-4">

                {hasCsvLocations && (
                    <div className="mb-6">
                        <label className="text-[14px] text-[#303030] font-medium font-poppins mb-2 block">
                            Select Locations from Uploaded CSV
                        </label>
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center">
                                <Checkbox
                                    checked={allSelected}
                                    onChecked={handleSelectAll}
                                    checkClassName="select-all-checkbox"
                                />
                                <span className="text-[13px] text-[#303030] font-normal ml-2 font-poppins cursor-pointer" onClick={handleSelectAll}>
                                    Select All
                                </span>
                            </div>

                            <span className="text-[12px] text-[#767676] font-poppins">
                                {selectedCount} Selected
                            </span>
                        </div>
                        <div className="flex flex-col gap-2">
                            {csvLocations.map((loc) => (
                                <div
                                    key={loc._id}
                                    className={`flex items-center p-3 border rounded-[6px] cursor-pointer transition-colors ${selectedLocationIds.includes(loc._id) ? 'bg-[#EBF9FE] border-[#00ADEF]' : 'bg-white border-[#E0E0E0]'}`}
                                    onClick={() => handleToggleLocation(loc)}
                                >
                                    <div className={`w-[19px] h-[19px] rounded-[4px] border flex items-center justify-center mr-3 flex-shrink-0 ${selectedLocationIds.includes(loc._id) ? 'bg-[#00ADEF] border-[#00ADEF]' : 'bg-white border-[#D9D9D9]'}`}>
                                        {selectedLocationIds.includes(loc._id) && <TickIcon className="w-3.5 h-3.5" />}
                                    </div>
                                    <span className="text-[13px] text-[#303030] font-poppins truncate">
                                        {loc?.googlePlaceId?.address || loc?.address || loc?.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Import Location File Section */}
                <div className="mb-6">
                    <label className="text-[14px] text-[#303030] font-[600] font-poppins mb-3 block">
                        Import Location File<span className="text-red-500">*</span>
                    </label>

                    {!uploadedFile ? (
                        <button
                            type="button"
                            onClick={handleUploadClick}
                            className="w-full h-[50px] bg-white border border-[#E0E0E0] rounded-[8px] flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors"
                        >
                            <div className="w-[24px] h-[24px] flex items-center justify-center">
                                <UploadExcelIcon />
                            </div>
                            <span className="text-[14px] text-[#303030] font-medium font-poppins">Upload CSV File</span>
                        </button>
                    ) : (
                        <div className="w-full h-[50px] bg-[#EBF9FE] border border-[#00ADEF] rounded-[8px] flex items-center justify-center px-4">
                            {/* Centered container: Icon + Name + Cross(Right Next) */}
                            <div className="flex items-center justify-center max-w-full">
                                <div className="w-[24px] h-[24px] flex items-center justify-center flex-shrink-0 mr-2">
                                    <UploadExcelIcon />
                                </div>
                                <span className="text-[14px] text-[#00ADEF] font-medium font-poppins truncate max-w-[220px]">
                                    {uploadedFile.name}
                                </span>
                                <button onClick={handleRemoveFile} type="button" className="p-0.5 hover:bg-[#D6F3FD] rounded-full flex-shrink-0 ml-1 flex items-center justify-center">
                                    <BlueCrossIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Download Sample File Section */}
                <div className="mb-3">
                    <label className="text-[14px] text-[#303030] font-[600] font-poppins mb-3 block">
                        Download Sample File
                    </label>
                    <button
                        type="button"
                        onClick={handleSampleDownload}
                        className="w-full h-[50px] bg-white border border-[#E0E0E0] rounded-[8px] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                    >
                        {/* Centered Item: Icon + Text + DownloadIcon(Strictly Next) */}
                        <div className="w-[24px] h-[24px] flex items-center justify-center">
                            <SampleUploadIcon />
                        </div>
                        <span className="text-[14px] text-[#303030] font-medium font-poppins">Sample File</span>
                        <DownloadIcon className="text-[#00ADEF] w-[20px] h-[20px] ml-1" />
                    </button>
                </div>

                {!uploadedFile && (
                    <div className="flex items-start gap-2 mt-4">
                        <InfoIcon className="w-[20px] h-[20px] flex-shrink-0" />
                        <p className="text-[12px] text-[#767676] font-poppins leading-[18px]">
                            Use the sample file as a reference to ensure your file includes the correct fields.
                        </p>
                    </div>
                )}
            </div>

            {/* Sticky Footer */}
            <div className="drawer-footer mt-auto pt-4 border-t border-[#F0F0F0] flex justify-end gap-6 bg-white z-10">
                <span className="cancel-link" onClick={onClose}>Cancel</span>
                <button
                    type="button"
                    className={`confirm-btn h-[40px] rounded-[5px] font-medium font-poppins text-[14px] text-white transition-colors ${!uploadedFile ? 'bg-[#B4E8FC] cursor-not-allowed' : 'bg-[#00ADEF] hover:bg-[#009BD6]'}`}
                    disabled={isSubmitting || !uploadedFile}
                    onClick={onClose}
                >
                    {isSubmitting ? "Adding..." : "Add"}
                </button>
            </div>
        </div>
    );
};

export default UploadCSVDrawer;
